import { ChatMessage } from './useChatSession';

interface MessageBubbleProps {
  message: ChatMessage;
  onFeedback?: (value: 'yes' | 'no') => void;
}

const MessageBubble = ({ message, onFeedback }: MessageBubbleProps) => {
  if (message.role === 'user') {
    return (
      <div className="flex flex-col items-end gap-2">
        <div
          className="max-w-[78%] text-white px-[15px] py-[9px] text-[14px] leading-[1.45]"
          style={{
            background: 'var(--chat-accent, #317690)',
            borderRadius: '16px 16px 4px 16px',
          }}
        >
          {message.text}
        </div>
      </div>
    );
  }

  // AI text bubble
  if (message.kind === 'text') {
    const displayText = message.displayText ?? message.text;
    const showCursor = message.streaming;

    return (
      <div className="flex flex-col items-start gap-2">
        <div
          className="max-w-[84%] bg-white border border-gray-200 text-gray-700 px-[15px] py-3 text-[14px] leading-[1.55] shadow-sm"
          style={{ borderRadius: '16px 16px 16px 4px' }}
        >
          {displayText || '...'}
          {showCursor && (
            <span
              className="inline-block w-[2px] h-[13px] ml-0.5 align-middle"
              style={{
                background: 'var(--chat-accent, #317690)',
                animation: 'hp-blink 1s step-start infinite',
              }}
            />
          )}
        </div>

        {/* Feedback row */}
        {message.done && !message.streaming && message.id !== 0 && (
          <div className="max-w-[84%] flex flex-col gap-2">
            {message.feedback ? (
              <span
                className="font-medium text-xs"
                style={{ color: 'var(--chat-accent, #317690)' }}
              >
                Thanks for the feedback!
              </span>
            ) : (
              <>
                <span className="text-xs text-gray-500">
                  Was this answer helpful?
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onFeedback?.('yes')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-white hover:opacity-90 transition-opacity"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z" />
                      <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
                    </svg>
                    Yes
                  </button>
                  <button
                    onClick={() => onFeedback?.('no')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-500 text-white hover:opacity-90 transition-opacity"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z" />
                      <path d="M17 2h3a2 2 0 012 2v7a2 2 0 01-2 2h-3" />
                    </svg>
                    No
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Rich provider card
  if (message.kind === 'card') {
    const initials = (message.providerName || 'P')
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

    return (
      <div className="flex flex-col items-start gap-2">
        <div className="max-w-[88%] w-[280px] bg-white border border-gray-200 rounded-2xl p-3.5 shadow-sm">
          <div className="flex gap-2.5 items-start">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-semibold text-[15px]"
              style={{
                background: 'var(--cyan-100, #E0F7FA)',
                color: 'var(--chat-accent, #317690)',
              }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm text-gray-700">
                {message.providerName}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {message.providerService}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2.5">
            <span
              className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                background: '#DCFCE7',
                color: '#16A34A',
              }}
            >
              {message.rating != null
                ? `★ ${message.rating.toFixed(1)}`
                : '★ New'}
            </span>
            <span className="text-xs font-medium text-gray-500">
              {message.reviewCount ?? 0} reviews
            </span>
            <span className="ml-auto font-semibold text-[13px] text-gray-700">
              {message.price != null ? `From $${message.price}/hr` : ''}
            </span>
          </div>

          <div className="mt-3">
            <a
              href={
                message.serviceId && message.slug
                  ? `/services/service-details/${message.displayId || message.serviceId}/${message.slug}`
                  : '/services/search-list'
              }
              className="block w-full text-center px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ background: '#317690' }}
            >
              Book This Provider
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MessageBubble;
